import { useEffect, useRef } from 'react';
import { isNil } from 'lodash';

// lodash `isEmpty` treats a numeric value (e.g. a sub-org id) as empty, so check explicitly.
export const hasOptionValue = (value: unknown): boolean => !isNil(value) && value !== '';

// Stable reference so the `options` default doesn't change identity each render and re-fire the effect.
const EMPTY_OPTIONS: never[] = [];

export interface UseAutoSelectSingleOptionParams<T> {
  enabled: boolean;
  isEmpty: boolean;
  // May be `undefined` while a data source is still loading (e.g. options derived from an in-flight fetch).
  options: T[] | undefined;
  onSelect: (option: T) => void;
  getOptionKey?: (option: T) => string;
}

// Auto-fills the sole option of a select when there is exactly one and the field is empty.
export const useAutoSelectSingleOption = <T>({
  enabled,
  isEmpty,
  options = EMPTY_OPTIONS,
  onSelect,
  getOptionKey = (option) => JSON.stringify(option),
}: UseAutoSelectSingleOptionParams<T>): void => {
  const filledKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || options.length !== 1) return;
    const [onlyOption] = options;
    const key = getOptionKey(onlyOption);
    // Remember any current value (incl. pre-filled) so a later deliberate clear is not re-filled.
    if (!isEmpty) {
      filledKeyRef.current = key;
      return;
    }
    if (filledKeyRef.current === key) return;
    filledKeyRef.current = key;
    onSelect(onlyOption);
  }, [enabled, isEmpty, options, onSelect, getOptionKey]);
};
